import { useCallback, useEffect, useState } from 'react';

import { CheckboxListItem } from '@screens/Category/ui/Filter/CheckboxListItem/CheckboxListItem';
import { FilterWrapper } from '@screens/Category/ui/Filter/FilterModal/FilterWrapper/FilterWrapper';
import { FieldLabels } from '@shared/types/enums/fieldLabels.enum';
import { Metrics } from '@shared/types/enums/metrics.enum';
import { OptionType } from '@shared/types/global';
import { Input } from '@shared/ui/Input';
import { sendMetrics } from '@shared/utils/sendMetrics/sendMetrics';

import cls from './CheckboxList.module.scss';

type CheckboxListProps = {
  label: string;
  options: OptionType[];
  onChange: (arr: OptionType[], filterId: string) => void;
  filterId: string;
  activeFilters: Record<string, string[] | number[] | boolean[]>;
};

export const CheckboxList = ({ label, options, filterId, onChange, activeFilters }: CheckboxListProps) => {
  const [hideOther, setHideOther] = useState<boolean>(true);
  const [maxElements, setMaxElements] = useState<number>(5);
  const [searchValue, setSearchValue] = useState<string>('');
  const [actualOptions, setActualOptions] = useState<OptionType[]>(options);
  const [filteredOptions, setFilteredOptions] = useState<OptionType[]>(options);
  const currentActiveFiltersLength = filteredOptions.filter((el) => el.active).length > 0;

  const onChangeSearchValue = (value: string) => {
    setSearchValue(value);
    setFilteredOptions(actualOptions.filter((el) => el.label.toLowerCase().includes(value.toLowerCase())));
    sendMetrics(Metrics.FILTER_GOAL);
  };

  const onChangeHandler = useCallback(
    (value: boolean, label: string) => {
      const newOptions = filteredOptions.map((el) => (el.label === label ? { ...el, active: value } : el));
      setActualOptions(newOptions);
      onChange(newOptions, filterId);
    },
    [filterId, filteredOptions, onChange]
  );

  const onResetFilter = () => {
    const newOptions = filteredOptions.map((el) => ({ ...el, active: false }));
    setActualOptions(newOptions);
    onChange(newOptions, filterId);
  };

  const changeMaxElements = () => {
    if (hideOther) {
      setHideOther(false);
      setMaxElements(options.length);
    } else {
      setHideOther(true);
      setMaxElements(5);
    }
  };

  const resetList = () => {
    setSearchValue('');
    setHideOther(true);
    setMaxElements(5);
    setFilteredOptions(actualOptions);
  };

  useEffect(() => {
    if (activeFilters && activeFilters[filterId]) {
      const activeLabels = new Set(activeFilters[filterId].filter((el) => typeof el === 'string'));
      const currentOptions = options.map((item) =>
        activeLabels.has(item.label)
          ? {
              ...item,
              active: true
            }
          : item
      );
      setFilteredOptions(currentOptions);
      setActualOptions(currentOptions);
      return;
    }

    setFilteredOptions(options);
    setActualOptions(options);
  }, [activeFilters, filterId, options]);

  return (
    <FilterWrapper
      resetActiveFilter={onResetFilter}
      isActiveFilter={currentActiveFiltersLength}
      variant={'checkbox-list'}
      onCloseCallBack={resetList}
      className={cls.wrap}
      label={label}
    >
      {options.length > 5 && (
        <div style={{ padding: '3px' }}>
          <Input
            size={'s'}
            value={searchValue}
            onChange={onChangeSearchValue}
            label={FieldLabels.SEARCH}
            className={cls.input}
            testId={'checkbox-list-input-search'}
          />
        </div>
      )}

      {filteredOptions.length > 0 ? (
        <div
          data-testid={'checkbox-list-options'}
          className={cls.scroll}
        >
          {filteredOptions.map((el, i) => {
            if (i <= maxElements) {
              return (
                <CheckboxListItem
                  key={i}
                  onChange={onChangeHandler}
                  label={el.label}
                  active={el.active}
                />
              );
            }
          })}
        </div>
      ) : (
        <div className={cls.notFound}>поиск не дал результатов</div>
      )}

      {filteredOptions.length > 5 && (
        <button
          data-testid={'checkbox-list-show-more-button'}
          onClick={changeMaxElements}
          className={cls.more}
        >
          {hideOther ? 'Показать еще' : 'Скрыть'}
        </button>
      )}
    </FilterWrapper>
  );
};
